import { toast } from "sonner";
import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import {
  PlaneKeyError,
  planeNotSyncedText,
  planeRefusal,
  type PlaneKeyStatus,
} from "../commons/planeKey";
import type {
  CompanyNote,
  ContactApprovalContactData,
  ContactApprovalReviewResult,
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import type { LoopsImportContact } from "../../contacts/showLeadImport";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import {
  combineMeetingTextIds,
  createTranscriptIdsMemo,
  type IdPage,
  type MeetingTextIds,
  type SummaryExcerpt,
  TRANSCRIPT_ID_LIMIT,
  type TranscriptExcerpt,
  transcriptQuery,
  withTranscriptIds,
} from "../commons/transcriptSearch";
import { getIsInitialized } from "./authProvider";
import { supabase } from "./supabase";

export type LoopsImportResult = {
  email: string;
  success: boolean;
  loopsContactId?: string;
  error?: string;
};

if (import.meta.env.VITE_SUPABASE_URL === undefined) {
  throw new Error("Please set the VITE_SUPABASE_URL environment variable");
}
if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
  throw new Error(
    "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
  );
}

const baseDataProvider = supabaseDataProvider({
  instanceUrl: import.meta.env.VITE_SUPABASE_URL,
  apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
  supabaseClient: supabase,
  sortOrder: "asc,desc.nullslast" as any,
});

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

// One round trip that makes a contact's links equal the given set. A delete followed by
// an insert from the client could leave a contact half-tagged if the second call failed;
// PostgREST cannot express set-equality against a junction table on its own.
const setContactTags = async (contactId: Identifier, tagIds: Identifier[]) => {
  const { error } = await supabase.rpc("set_contact_tags", {
    p_contact_id: contactId,
    p_tag_ids: tagIds,
  });
  if (error) throw error;
};

// Same shape and same reason as setContactTags above, for rel__contacts__deals.
const setDealContacts = async (
  dealId: Identifier,
  contactIds: Identifier[],
) => {
  const { error } = await supabase.rpc("set_deal_contacts", {
    p_deal_id: dealId,
    p_contact_ids: contactIds,
  });
  if (error) throw error;
};

// Module-level on purpose: the list reaches it through the data provider, not
// react-query, so this is the one place both callers pass through.
// One id search RPC. Each returns up to the limit plus one row, which is how the
// cap is detected.
const searchIds = async (
  fn: "search_transcript_ids" | "search_summary_ids",
  query: string,
): Promise<IdPage> => {
  const { data, error } = await supabase.rpc(fn, {
    p_query: query,
    p_limit: TRANSCRIPT_ID_LIMIT,
  });
  if (error) throw error;
  const ids = ((data ?? []) as { meeting_id: Identifier }[]).map(
    (row) => row.meeting_id,
  );
  return {
    ids: ids.slice(0, TRANSCRIPT_ID_LIMIT),
    capped: ids.length > TRANSCRIPT_ID_LIMIT,
  };
};

// Transcript and summary matches (LCATOMICZO-76 phases 4 and 5), fetched
// together so one failing does not hide the other.
const transcriptIdsMemo = createTranscriptIdsMemo(async (query) => {
  const [transcript, summary] = await Promise.allSettled([
    searchIds("search_transcript_ids", query),
    searchIds("search_summary_ids", query),
  ]);
  return combineMeetingTextIds(transcript, summary);
});

const dataProviderWithCustomMethods = {
  ...baseDataProvider,
  async getList(resource: string, params: GetListParams) {
    if (resource === "companies") {
      return baseDataProvider.getList("companies_summary", params);
    }
    if (resource === "contacts") {
      return baseDataProvider.getList("contacts_summary", params);
    }
    // Deal contacts live in rel__contacts__deals. deals_summary serves them back as a
    // contact_ids bigint[] of the same name and type, so the board, the show page and the
    // `contact_ids@cs` filters see no difference. LCATOMICZO-56.
    if (resource === "deals") {
      return baseDataProvider.getList("deals_summary", params);
    }
    if (resource === "activity_log") {
      const { data, total } = await baseDataProvider.getList(
        "activity_log",
        params,
      );
      // Rename snake_case view columns to camelCase to match Activity type
      return {
        data: data.map((row: any) => ({
          ...row,
          contactNote: row.contact_note ?? undefined,
          dealNote: row.deal_note ?? undefined,
          companyNote: row.company_note ?? undefined,
          contact_note: undefined,
          deal_note: undefined,
          company_note: undefined,
        })),
        total,
      };
    }

    if (resource === "zoom_meetings") {
      return baseDataProvider.getList("zoom_meetings_summary", params);
    }

    return baseDataProvider.getList(resource, params);
  },
  async getOne(resource: string, params: any) {
    if (resource === "companies") {
      return baseDataProvider.getOne("companies_summary", params);
    }
    if (resource === "contacts") {
      return baseDataProvider.getOne("contacts_summary", params);
    }
    // Unlike zoom_meetings below, deals getOne DOES read the view. Once task 3 drops
    // deals.contact_ids the base table has no such column, so a base-table getOne would leave
    // the show and edit pages with no contacts at all. The write path is intercepted instead.
    if (resource === "deals") {
      return baseDataProvider.getOne("deals_summary", params);
    }
    // NB: zoom_meetings getOne reads the BASE table, not the zoom_meetings_summary
    // view (unlike getList/getMany). The meeting detail page edits this record and
    // writes back via update("zoom_meetings") — if the record carried the view's
    // aggregate columns (has_summary, task_count, …) the PATCH would try to write
    // non-existent base columns and 400. The show/edit only needs base columns.

    return baseDataProvider.getOne(resource, params);
  },
  async getMany(resource: string, params: any) {
    // Mirror getOne/getList so references (ReferenceField uses getMany) receive
    // the enriched summary-view records — e.g. companies_summary.domain, which
    // the CompanyAvatar needs to resolve a favicon on the contact detail page.
    if (resource === "companies") {
      return baseDataProvider.getMany("companies_summary", params);
    }
    if (resource === "contacts") {
      return baseDataProvider.getMany("contacts_summary", params);
    }
    if (resource === "deals") {
      return baseDataProvider.getMany("deals_summary", params);
    }
    if (resource === "zoom_meetings") {
      return baseDataProvider.getMany("zoom_meetings_summary", params);
    }

    return baseDataProvider.getMany(resource, params);
  },

  // Tag membership lives in rel__contacts__tags. `contacts` no longer has a writable
  // tags column, but every caller still passes `tags` on the record — the contact form,
  // TagsListEdit, both create paths, the approval inbox, both importers, and the merge.
  // Intercepting here rather than editing each of them keeps one rule in one place:
  // strip tags off the row write, then set the links.
  //
  // Reads already return `tags` as an aggregate from contacts_summary, so the field
  // round-trips and callers see no difference.
  async create(resource: string, params: any) {
    // Deal contacts live in rel__contacts__deals, same arrangement as contact tags below.
    // deals_summary reads them back as contact_ids, so the deal form and the Zoom summary's
    // deal-create keep sending a bigint[] and neither knows the difference. LCATOMICZO-56.
    if (resource === "deals") {
      const { contact_ids, ...data } = params.data ?? {};
      const result = await baseDataProvider.create("deals", {
        ...params,
        data,
      });
      if (Array.isArray(contact_ids) && contact_ids.length > 0) {
        await setDealContacts(result.data.id, contact_ids);
      }
      return { data: { ...result.data, contact_ids: contact_ids ?? [] } };
    }
    if (resource !== "contacts") {
      return baseDataProvider.create(resource, params);
    }
    const { tags, ...data } = params.data ?? {};
    const result = await baseDataProvider.create("contacts", {
      ...params,
      data,
    });
    if (Array.isArray(tags) && tags.length > 0) {
      await setContactTags(result.data.id, tags);
    }
    return { data: { ...result.data, tags: tags ?? [] } };
  },

  async update(resource: string, params: any) {
    if (resource === "deals") {
      const { contact_ids, ...data } = params.data ?? {};
      const result = await baseDataProvider.update("deals", {
        ...params,
        data,
      });
      // `undefined` means the caller was not editing contacts at all, which is different from
      // an empty array — that one means "remove them all" and has to reach the database.
      // Collapsing the two would wipe a deal's contacts on any unrelated edit, such as
      // renaming it.
      if (contact_ids !== undefined) {
        await setDealContacts(params.id, contact_ids ?? []);
      }
      return {
        data: {
          ...result.data,
          ...(contact_ids !== undefined ? { contact_ids } : {}),
        },
      };
    }
    if (resource !== "contacts") {
      return baseDataProvider.update(resource, params);
    }
    const { tags, ...data } = params.data ?? {};
    const result = await baseDataProvider.update("contacts", {
      ...params,
      data,
    });
    // `undefined` means the caller was not editing tags at all, which is different from
    // an empty array — that one means "remove them all" and has to reach the database.
    if (tags !== undefined) {
      await setContactTags(params.id, tags ?? []);
    }
    return {
      data: { ...result.data, ...(tags !== undefined ? { tags } : {}) },
    };
  },

  async salesCreate(body: SalesFormData) {
    const { data, error } = await supabase.functions.invoke<{ data: Sale }>(
      "users",
      {
        method: "POST",
        body,
      },
    );

    if (!data || error) {
      console.error("salesCreate.error", error);
      const errorDetails = await (async () => {
        try {
          return (await error?.context?.json()) ?? {};
        } catch {
          return {};
        }
      })();
      throw new Error(errorDetails?.message || "Failed to create the user");
    }

    return data.data;
  },
  async salesUpdate(
    id: Identifier,
    data: Partial<Omit<SalesFormData, "password">>,
  ) {
    const {
      email,
      first_name,
      last_name,
      administrator,
      avatar,
      disabled,
      role,
    } = data;

    const { data: updatedData, error } = await supabase.functions.invoke<{
      data: Sale;
    }>("users", {
      method: "PATCH",
      body: {
        sales_id: id,
        email,
        first_name,
        last_name,
        administrator,
        disabled,
        avatar,
        role,
      },
    });

    if (!updatedData || error) {
      console.error("salesCreate.error", error);
      throw new Error("Failed to update account manager");
    }

    return updatedData.data;
  },
  async updatePassword(id: Identifier) {
    const { data: passwordUpdated, error } =
      await supabase.functions.invoke<boolean>("update_password", {
        method: "PATCH",
        body: {
          sales_id: id,
        },
      });

    if (!passwordUpdated || error) {
      console.error("update_password.error", error);
      throw new Error("Failed to update password");
    }

    return passwordUpdated;
  },
  async unarchiveDeal(deal: Deal) {
    // get all deals where stage is the same as the deal to unarchive
    const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
      filter: { stage: deal.stage },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "index", order: "ASC" },
    });

    // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
    const updatedDeals = deals.map((d, index) => ({
      ...d,
      index: d.id === deal.id ? 0 : index + 1,
      archived_at: d.id === deal.id ? null : d.archived_at,
    }));

    return await Promise.all(
      updatedDeals.map((updatedDeal) =>
        baseDataProvider.update("deals", {
          id: updatedDeal.id,
          data: updatedDeal,
          previousData: deals.find((d) => d.id === updatedDeal.id),
        }),
      ),
    );
  },
  async isInitialized() {
    return getIsInitialized();
  },
  async mergeContacts(sourceId: Identifier, targetId: Identifier) {
    const { data, error } = await supabase.functions.invoke("merge_contacts", {
      method: "POST",
      body: { loserId: sourceId, winnerId: targetId },
    });

    if (error) {
      console.error("merge_contacts.error", error);
      throw new Error("Failed to merge contacts");
    }

    return data;
  },
  async linkToLoops(contactId: Identifier) {
    const { data, error } = await supabase.functions.invoke("loops_link", {
      method: "POST",
      body: { contact_id: contactId },
    });

    if (error) {
      console.error("loops_link.error", error);
      throw new Error("Failed to link contact to Loops");
    }

    return data;
  },
  // Bulk-create show-lead contacts in Loops with full property fidelity
  // (userGroup, tags, source, ...) and stamp loops_contact_id back on the
  // matching CRM rows. Used by the Contacts CSV import when a Loops-format
  // show-lead file is detected.
  async importContactsToLoops(
    contacts: LoopsImportContact[],
  ): Promise<{ results: LoopsImportResult[] }> {
    const { data, error } = await supabase.functions.invoke("loops_import", {
      method: "POST",
      body: { contacts },
    });

    if (error) {
      console.error("loops_import.error", error);
      throw new Error("Failed to import contacts to Loops");
    }

    return data as { results: LoopsImportResult[] };
  },
  async reviewContactApproval(
    approvalId: Identifier,
    decision: "approve" | "reject",
    contact: ContactApprovalContactData | null = null,
  ): Promise<ContactApprovalReviewResult> {
    const { data, error } = await supabase.rpc("review_contact_approval", {
      p_approval_id: approvalId,
      p_decision: decision,
      p_contact: contact,
    });

    if (error) throw error;
    const result = (Array.isArray(data) ? data[0] : data) as
      | ContactApprovalReviewResult
      | undefined;
    if (!result) throw new Error("Contact approval returned no result");
    return result;
  },
  async pushTasksToPlane(
    taskIds: Identifier[],
  ): Promise<{ results: PlanePushResult[] }> {
    const { data, error } = await supabase.functions.invoke("plane_push", {
      method: "POST",
      body: { taskIds },
    });

    if (error) {
      console.error("plane_push.error", error);
      const errorDetails = await (async () => {
        try {
          return (await error?.context?.json()) ?? {};
        } catch {
          return {};
        }
      })();
      throw new Error(errorDetails?.message || "Failed to push tasks to Plane");
    }

    return data;
  },
  /**
   * Whether the signed-in user has a Plane API key stored, and whose it is. The
   * key itself never comes back: it lives in a table only the service role can
   * read. LCCRM-92.
   */
  async getPlaneKeyStatus(): Promise<PlaneKeyStatus> {
    const { data, error } = await supabase.functions.invoke("plane_key", {
      method: "GET",
    });
    if (error) throw await planeKeyError(error);
    return data;
  },
  /** Checks the key with Plane and stores it. Throws a PlaneKeyError on refusal. */
  async savePlaneKey(apiKey: string): Promise<PlaneKeyStatus> {
    const { data, error } = await supabase.functions.invoke("plane_key", {
      method: "POST",
      body: { apiKey },
    });
    if (error) throw await planeKeyError(error);
    return data;
  },
  async removePlaneKey(): Promise<PlaneKeyStatus> {
    const { data, error } = await supabase.functions.invoke("plane_key", {
      method: "DELETE",
    });
    if (error) throw await planeKeyError(error);
    return data;
  },
  /**
   * Uploads an image pasted into a note straight away, because the note text
   * needs the image's address before the note is saved. What comes back goes
   * into the note's attachments as it is: it already has its `path`, so the
   * save does not upload it again and the cleanup trigger deletes it with the
   * note. LCCRM-87.
   */
  async uploadNoteImage(file: File): Promise<Omit<RAFile, "rawFile">> {
    const objectUrl = URL.createObjectURL(file);
    try {
      const { src, path, title, type } = await uploadToBucket<RAFile>({
        rawFile: file,
        src: objectUrl,
        title: file.name || "image",
      });
      return { src, path, title, type };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  },
  /**
   * Meetings whose transcript or summary matches `q`, for the meetings list
   * search. One request pair per settled query, shared with the list's own
   * getList (see createTranscriptIdsMemo). A query too short to search returns
   * nothing.
   */
  async searchMeetingText(q: string): Promise<MeetingTextIds> {
    const query = transcriptQuery(q);
    if (!query) {
      return { ids: [], transcriptIds: [], summaryIds: [], capped: false };
    }
    return transcriptIdsMemo(query);
  },
  /** Where each meeting's transcript matches `q`, for the rows on screen. */
  async transcriptExcerpts(
    q: string,
    meetingIds: Identifier[],
  ): Promise<TranscriptExcerpt[]> {
    const query = transcriptQuery(q);
    if (!query || !meetingIds.length) return [];
    const { data, error } = await supabase.rpc("transcript_excerpts", {
      p_query: query,
      p_meeting_ids: meetingIds,
    });
    if (error) throw error;
    return ((data ?? []) as TranscriptExcerpt[]).map((row) => ({
      ...row,
      cue_start: Number(row.cue_start),
    }));
  },
  /** Which summary of each meeting matches `q`, for the rows on screen. */
  async summaryExcerpts(
    q: string,
    meetingIds: Identifier[],
  ): Promise<SummaryExcerpt[]> {
    const query = transcriptQuery(q);
    if (!query || !meetingIds.length) return [];
    const { data, error } = await supabase.rpc("summary_excerpts", {
      p_query: query,
      p_meeting_ids: meetingIds,
    });
    if (error) throw error;
    return (data ?? []) as SummaryExcerpt[];
  },
  async getConfiguration(): Promise<ConfigurationContextValue> {
    const { data } = await baseDataProvider.getOne("configuration", { id: 1 });
    return (data?.config as ConfigurationContextValue) ?? {};
  },
  async updateConfiguration(
    config: ConfigurationContextValue,
  ): Promise<ConfigurationContextValue> {
    const { data } = await baseDataProvider.update("configuration", {
      id: 1,
      data: { config },
      previousData: { id: 1 },
    });
    return data.config as ConfigurationContextValue;
  },
} satisfies DataProvider;

// Per-task outcome from the plane_push edge function. `ok:false` with
// reason "assignee_not_member" is an expected skip (rep not on Plane), not an
// error — the UI surfaces those users so they can be onboarded and re-pushed.
const planeKeyError = async (error: {
  context?: { json?: () => Promise<unknown> };
}): Promise<PlaneKeyError> => {
  let details: { message?: string; reason?: string; project?: string } = {};
  try {
    details = ((await error?.context?.json?.()) ?? {}) as typeof details;
  } catch {
    // Not a JSON body (network failure, gateway error): keep the generic message.
  }
  const e = new PlaneKeyError(details.message || "Plane key request failed");
  e.reason = details.reason as PlaneKeyError["reason"];
  e.project = details.project;
  return e;
};

export type { PlaneKeyStatus };
export { PlaneKeyError };

export type PlanePushResult = {
  id: Identifier;
  ok: boolean;
  identifier?: string;
  // The last two are about the person pushing (their Plane key), and are the same
  // for every task in one request. LCCRM-92.
  reason?: "assignee_not_member" | "no_plane_key" | "plane_key_rejected";
  assignee?: { name: string | null; email: string | null };
  error?: string;
};

export type CrmDataProvider = typeof dataProviderWithCustomMethods;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

// Background re-push of already-linked tasks after a CRM edit. Fire-and-forget:
// the user's mutation must not wait on (or fail because of) Plane. plane_push
// PATCHes when plane_issue_id is set, so this never creates new work items —
// with `checkLinked`, unlinked ids are filtered out first (updateMany callbacks
// only carry ids, not records).
const resyncTasksWithPlane = (
  taskIds: Identifier[],
  { checkLinked = false }: { checkLinked?: boolean } = {},
): void => {
  void (async () => {
    let ids = taskIds;
    if (checkLinked) {
      const { data, error } = await supabase
        .from("tasks")
        .select("id")
        .in("id", taskIds)
        .not("plane_issue_id", "is", null);
      if (error) throw new Error(error.message);
      ids = (data ?? []).map((t) => t.id);
    }
    if (!ids.length) return;
    const { results } =
      await dataProviderWithCustomMethods.pushTasksToPlane(ids);
    // The edit is already saved; only the copy in Plane is stale. A push needs
    // the editor's own Plane key (LCCRM-92), and this runs outside React, so the
    // warning goes straight to the toaster. One id, so a burst of edits shows a
    // single toast rather than a stack.
    const refusal = planeRefusal(results);
    if (refusal) {
      toast.warning(planeNotSyncedText(refusal), { id: "plane-not-synced" });
    }
  })().catch((error) => {
    console.error("plane_resync.error", error);
  });
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "company_notes",
    beforeSave: async (data: CompanyNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    // The channel picker searches as it is typed in; `q` is not a column.
    resource: "note_channels",
    beforeGetList: async (params) => applyFullTextSearch(["name"])(params),
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyStableSort(
        applyFullTextSearch([
          "first_name",
          "last_name",
          "company_name",
          "title",
          "email",
          "phone",
          "background",
        ])(params),
      );
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyStableSort(
        applyFullTextSearch([
          "name",
          "phone_number",
          "website",
          "zipcode",
          "city",
          "state_abbr",
        ])(params),
      );
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyStableSort(
        applyFullTextSearch(["first_name", "last_name"])(params),
      );
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      if (!params.filter?.q) return applyStableSort(params);
      const { q, ...filter } = params.filter;
      return applyStableSort({
        ...params,
        filter: {
          ...filter,
          "@or": {
            "first_name@ilike": q,
            "last_name@ilike": q,
            "email@ilike": q,
          },
        },
      });
    },
  },
  {
    // The Action Items search box sends `q`; map it to a text search so
    // PostgREST doesn't try to filter a non-existent `tasks.q` column.
    resource: "tasks",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["text"])(params);
    },
    // Auto re-sync: any CRM edit to a Plane-linked task re-pushes it so the
    // work item's title, dates, assignee, and state stay current. Loop-safe:
    // the Plane webhook writes back via the service role (never through this
    // provider), so inbound syncs can't re-trigger an outbound push.
    afterUpdate: async (result) => {
      if (result.data?.plane_issue_id) {
        resyncTasksWithPlane([result.data.id]);
      }
      return result;
    },
    afterUpdateMany: async (result) => {
      // updateMany only returns ids — look up which of them are linked.
      if (result.data?.length) {
        resyncTasksWithPlane(result.data, { checkLinked: true });
      }
      return result;
    },
  },
  {
    // Zoom meetings list search. Runs before the getList remap to the
    // zoom_meetings_summary view, so the fields must be view columns.
    resource: "zoom_meetings",
    beforeGetList: async (params) => {
      const searched = applyFullTextSearch([
        // A name given in Atomic is what the user remembers it by (LCCRM-88).
        "custom_title",
        "meeting_topic",
        "meeting_host_email",
        "summary_title",
        // Attendee name is the only identity Zoom gives us for external
        // participants, so it is often the only way to find their calls.
        // The text form exists because ilike cannot be applied to the
        // participant_names array (LCATOMICZO-45).
        "participant_names_text",
      ])(params);
      // Words spoken in the meeting or written in its summaries (LCATOMICZO-76).
      // A failure here must not take the list down with it, so it falls back to
      // the field search.
      const query = transcriptQuery(params.filter?.q);
      if (!query) return applyStableSort(searched);
      try {
        const { ids } = await transcriptIdsMemo(query);
        return applyStableSort(withTranscriptIds(searched, ids));
      } catch (error) {
        console.error("Transcript search failed; using field search", error);
        return applyStableSort(searched);
      }
    },
  },
];

export const dataProvider = withLifecycleCallbacks(
  dataProviderWithCustomMethods,
  lifeCycleCallbacks,
) as CrmDataProvider;

/**
 * PostgREST returns rows with equal sort values in nondeterministic order, so
 * paginated/infinite lists can repeat or skip records across pages (bulk
 * imports share timestamps like last_seen). Append the primary key as a sort
 * tiebreaker. Relies on ra-data-postgrest's getOrderBy emitting
 * `${field}.${dir}` verbatim: rewriting the field to "last_seen.desc,id" at
 * request time yields the valid compound order "last_seen.desc,id.desc".
 */
const applyStableSort = (params: GetListParams): GetListParams => {
  const { field, order } = params.sort ?? {};
  // order is optional on GetListParams["sort"] destructured this way, and it is
  // interpolated below, so a missing one has to fall out here with field.
  if (
    !field ||
    !order ||
    field === "id" ||
    field.includes(",") ||
    field.includes(".")
  ) {
    return params;
  }
  return {
    ...params,
    sort: { field: `${field}.${order.toLowerCase()},id`, order },
  };
};

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce(
        (acc, column) => ({
          ...acc,
          [`${column}@ilike`]: q,
        }),
        {},
      ),
    },
  };
};

// Takes a Partial<RAFile> because that is what the record types actually hold: a
// stored avatar or logo has src and path, and rawFile exists only while a file is
// being uploaded. The body already anticipated a missing src further down; the first
// read did not, so an avatar without one crashed here rather than falling through.
// Generic so it hands back exactly the type it was given: it mutates and returns the
// same object, so an AttachmentNote in means an AttachmentNote out.
const uploadToBucket = async <T extends Partial<RAFile>>(fi: T): Promise<T> => {
  if (!fi.src?.startsWith("blob:") && !fi.src?.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await supabase.storage
        .from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  // The content can come from a fetch of src rather than from a picked file, so
  // rawFile is not guaranteed here. Without it there is no name to take an extension
  // from and no MIME type to record; the upload itself still works.
  const file = fi.rawFile;
  const fileParts = file?.name.split(".") ?? [];
  const fileExt = fileParts.length > 1 ? `.${fileParts.pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  if (file) fi.type = file.type;

  return fi;
};
